import { useMemo } from 'react';
import usePeriodontalData, {
	DEFAULT_PERIODONTAL_DATA,
} from './usePeriodontalData';
import { PeriodontalToothView } from '../../../Interfaces/PeriodontalToothView.type';
import { useDENTContext } from '../../../Services/DENTContext';
import { Tooth } from '../../../../../graphql/graphql';

interface UsePeriodontalPointsMouthProps {
	base: { w: number; h: number };
	defaultY: number;
	middlePointY: number;
	distance: number;
	margins: number[];
	depths: number[];
	leftNeighbor?: Tooth;
	rightNeighbor?: Tooth;
	view: PeriodontalToothView;
}

// X positions are the same for both margin and depth points
const getXPositions = (width: number) => [
	0, // Far left
	width / 5, // Left-middle
	width / 2, // Middle
	(width * 4) / 5, // Right-middle
	width, // Far right
];

export default function usePeriodontalPointsMouth({
	base,
	defaultY,
	middlePointY,
	distance,
	margins,
	depths,
	leftNeighbor,
	rightNeighbor,
	view,
}: UsePeriodontalPointsMouthProps) {
	const { hideMiddlePeriodontalItems } = useDENTContext();

	// Calculate neighbor margins and depths using the same view as current tooth
	const leftNeighborData = leftNeighbor
		? usePeriodontalData({
				periodontal: leftNeighbor.periodontal,
				view,
				side: leftNeighbor.side,
		  })
		: DEFAULT_PERIODONTAL_DATA;

	const rightNeighborData = rightNeighbor
		? usePeriodontalData({
				periodontal: rightNeighbor.periodontal,
				view,
				side: rightNeighbor.side,
		  })
		: DEFAULT_PERIODONTAL_DATA;
	// Extract common neighbor value logic
	// The margins array is always [left, middle, right] from the tooth's perspective in that view
	// Left neighbor's rightmost value (closest to current tooth) = margins[2]
	// Right neighbor's leftmost value (closest to current tooth) = margins[0]
	const neighborValues = useMemo(() => {
		return {
			leftNeighborRightMiddleMargin: leftNeighborData.margins[2],
			leftNeighborRightMiddleDepth: leftNeighborData.depths[2],
			rightNeighborLeftMiddleMargin: rightNeighborData.margins[0],
			rightNeighborLeftMiddleDepth: rightNeighborData.depths[0],
		};
	}, [leftNeighborData, rightNeighborData]);

	// Current tooth's Y positions (used by both margin and depth)
	const currentToothYPositions = useMemo(() => {
		const leftMiddleMarginY = defaultY - margins[0] * distance;
		const rightMiddleMarginY = defaultY - margins[2] * distance;
		// When hideMiddlePeriodontalItems is true, interpolate middle margin value but keep middlePointY offset
		const middleMargin = hideMiddlePeriodontalItems
			? (margins[0] + margins[2]) / 2
			: margins[1];
		const centerMarginY = middlePointY - middleMargin * distance;

		const leftMiddleY = leftMiddleMarginY + depths[0] * distance;
		const rightMiddleY = rightMiddleMarginY + depths[2] * distance;
		// When hideMiddlePeriodontalItems is true, interpolate middle depth Y position between left and right
		const centerY = hideMiddlePeriodontalItems
			? Math.max((leftMiddleY + rightMiddleY) / 2, centerMarginY)
			: centerMarginY + depths[1] * distance;

		return {
			leftMiddleMarginY,
			centerMarginY,
			rightMiddleMarginY,
			leftMiddleY,
			centerY,
			rightMiddleY,
		};
	}, [
		defaultY,
		middlePointY,
		distance,
		margins,
		depths,
		hideMiddlePeriodontalItems,
	]);

	// Neighbor Y positions (used by both margin and depth)
	const neighborYPositions = useMemo(() => {
		const leftNeighborRightMiddleMarginY =
			defaultY - neighborValues.leftNeighborRightMiddleMargin * distance;
		const leftNeighborRightMiddleY =
			leftNeighborRightMiddleMarginY +
			neighborValues.leftNeighborRightMiddleDepth * distance;

		const rightNeighborLeftMiddleMarginY =
			defaultY - neighborValues.rightNeighborLeftMiddleMargin * distance;
		const rightNeighborLeftMiddleY =
			rightNeighborLeftMiddleMarginY +
			neighborValues.rightNeighborLeftMiddleDepth * distance;

		return {
			leftNeighborRightMiddleMarginY,
			leftNeighborRightMiddleY,
			rightNeighborLeftMiddleMarginY,
			rightNeighborLeftMiddleY,
		};
	}, [defaultY, distance, neighborValues]);

	const marginPoints = useMemo(() => {
		const leftMiddleY = currentToothYPositions.leftMiddleMarginY;
		const centerY = currentToothYPositions.centerMarginY;
		const rightMiddleY = currentToothYPositions.rightMiddleMarginY;

		const farLeftY =
			(neighborYPositions.leftNeighborRightMiddleMarginY + leftMiddleY) / 2;
		const farRightY =
			(rightMiddleY + neighborYPositions.rightNeighborLeftMiddleMarginY) / 2;

		const xPositions = getXPositions(base.w);
		return [
			{ x: xPositions[0], y: farLeftY },
			{ x: xPositions[1], y: leftMiddleY },
			{ x: xPositions[2], y: centerY },
			{ x: xPositions[3], y: rightMiddleY },
			{ x: xPositions[4], y: farRightY },
		];
	}, [base.w, currentToothYPositions, neighborYPositions]);

	const depthPoints = useMemo(() => {
		const { leftMiddleY, centerY, rightMiddleY } = currentToothYPositions;

		const farLeftY =
			(neighborYPositions.leftNeighborRightMiddleY + leftMiddleY) / 2;
		const farRightY =
			(rightMiddleY + neighborYPositions.rightNeighborLeftMiddleY) / 2;

		const xPositions = getXPositions(base.w);
		return [
			{ x: xPositions[0], y: farLeftY },
			{ x: xPositions[1], y: leftMiddleY },
			{ x: xPositions[2], y: centerY },
			{ x: xPositions[3], y: rightMiddleY },
			{ x: xPositions[4], y: farRightY },
		];
	}, [base.w, currentToothYPositions, neighborYPositions]);

	return { marginPoints, depthPoints };
}
